import { useCallback, useEffect, useRef, useState } from 'react';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';

/** One listing photo at the three sizes the gallery needs (ImageVariants 400/800/1200). */
export interface GalleryPhoto {
  thumb: string;
  tile: string;
  full: string;
}

const MOSAIC_MAX = 5;

// The business-page gallery. Desktop: a mosaic sized to the photos actually uploaded
// (one large + up to four — never a photo repeated to fill a tile), every tile opens
// the lightbox on that photo, the last tile says "+N" when more are hidden, and "Show
// all photos" sits on the image. Mobile: a full-bleed snap carousel with a counter and
// dots; a tap opens the lightbox. `note` labels where the photos came from (an
// unclaimed listing, FR-A25); `onVideo` adds a "Video" pill that jumps to the video.
export function BusinessGallery({
  photos,
  name,
  note,
  onVideo,
}: {
  photos: GalleryPhoto[];
  name: string;
  note?: string;
  onVideo?: () => void;
}) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState<number | null>(null);
  const [slide, setSlide] = useState(0);
  const close = useCallback(() => setOpen(null), []);
  if (photos.length === 0) return null;

  const shown = photos.slice(0, MOSAIC_MAX);
  const hidden = photos.length - shown.length;
  const count = (n: number) => localiseDigits(lang, n);

  const pills = (
    <div className="biz-gal__pills">
      {onVideo && (
        <button type="button" className="biz-gal__pill" onClick={onVideo}>
          <span aria-hidden="true">▶</span> {t('biz.video')}
        </button>
      )}
      <button type="button" className="biz-gal__pill" onClick={() => setOpen(0)}>
        <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14">
          <path d="M1 1h6v6H1zM9 1h6v6H9zM1 9h6v6H1zM9 9h6v6H9z" fill="currentColor" />
        </svg>
        {t('biz.show_all_photos', { n: count(photos.length) })}
      </button>
    </div>
  );

  return (
    <>
      {/* desktop mosaic */}
      <div className="biz-gal">
        <div className={`biz-mosaic biz-mosaic--n${shown.length}`}>
          {shown.map((p, i) => (
            <button
              key={i}
              type="button"
              className="biz-mosaic__tile"
              onClick={() => setOpen(i)}
              aria-label={t('biz.photo_open', { n: count(i + 1), total: count(photos.length) })}
            >
              <img src={i === 0 ? p.full : p.tile} alt={name} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" />
              {i === shown.length - 1 && hidden > 0 && (
                <span className="biz-mosaic__more" aria-hidden="true">
                  +{count(hidden)}
                </span>
              )}
            </button>
          ))}
        </div>
        {note && <span className="biz-gal__note">{note}</span>}
        {pills}
      </div>

      {/* mobile carousel */}
      <div className="biz-swipe">
        <div
          className="biz-swipe__strip"
          // DC123: a scrollable strip must be keyboard-reachable (axe scrollable-region-focusable).
          role="region"
          tabIndex={0}
          aria-label={t('a11y.gallery', { name })}
          onScroll={(e) => {
            const el = e.currentTarget;
            const idx = Math.round(el.scrollLeft / el.clientWidth);
            if (idx !== slide) setSlide(idx);
          }}
        >
          {photos.map((p, i) => (
            <button key={i} type="button" className="biz-swipe__slide" onClick={() => setOpen(i)} aria-label={t('biz.photo_open', { n: count(i + 1), total: count(photos.length) })}>
              <img loading={i === 0 ? 'eager' : 'lazy'} decoding="async" src={p.tile} alt={name} />
            </button>
          ))}
        </div>
        {note && <span className="biz-swipe__note">{note}</span>}
        <span className="biz-swipe__count">{localiseDigits(lang, `${Math.min(slide, photos.length - 1) + 1}/${photos.length}`)}</span>
        {photos.length > 1 && (
          <div className="biz-swipe__dots" aria-hidden="true">
            {photos.map((_, i) => (
              <span key={i} className={i === slide ? 'biz-swipe__dot is-on' : 'biz-swipe__dot'} />
            ))}
          </div>
        )}
        {onVideo && (
          <button type="button" className="biz-swipe__video" onClick={onVideo}>
            <span aria-hidden="true">▶</span> {t('biz.video')}
          </button>
        )}
      </div>

      {open !== null && <PhotoLightbox photos={photos} start={open} name={name} onClose={close} />}
    </>
  );
}

// Full-screen photo viewer: one photo at a time, fitted (never cropped), with prev/next
// arrows, ←/→/Esc keys, swipe on touch, a counter and a thumbnail strip. Locks the page
// scroll while open, preloads the neighbours so paging is instant, and hands focus back
// to whatever opened it.
export function PhotoLightbox({ photos, start, name, onClose }: { photos: GalleryPhoto[]; start: number; name: string; onClose: () => void }) {
  const { t, lang } = useLanguage();
  const [idx, setIdx] = useState(start);
  const closeRef = useRef<HTMLButtonElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const touchX = useRef<number | null>(null);
  const n = photos.length;
  const go = useCallback((step: number) => setIdx((i) => (i + step + n) % n), [n]);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [onClose, go]);

  useEffect(() => {
    for (const j of [idx + 1, idx - 1]) {
      const p = photos[(j + n) % n];
      if (p && typeof Image !== 'undefined') new Image().src = p.full;
    }
    const thumb = stripRef.current?.children[idx] as HTMLElement | undefined;
    thumb?.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [idx, n, photos]);

  const photo = photos[idx]!;
  return (
    <div className="biz-lb" role="dialog" aria-modal="true" aria-label={t('a11y.gallery', { name })}>
      <div className="biz-lb__bar">
        <span className="biz-lb__count">{localiseDigits(lang, `${idx + 1} / ${n}`)}</span>
        <span className="biz-lb__name">{name}</span>
        <button ref={closeRef} type="button" className="biz-lb__close" onClick={onClose} aria-label={t('biz.uc.close_photos')}>
          ✕
        </button>
      </div>
      <div
        className="biz-lb__stage"
        onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const startX = touchX.current;
          const endX = e.changedTouches[0]?.clientX;
          touchX.current = null;
          if (startX == null || endX == null || Math.abs(endX - startX) < 40) return;
          go(endX < startX ? 1 : -1);
        }}
      >
        <img key={idx} className="biz-lb__img" src={photo.full} loading="eager" alt={`${name} — ${localiseDigits(lang, `${idx + 1}/${n}`)}`} decoding="async" />
        {n > 1 && (
          <>
            <button type="button" className="biz-lb__nav biz-lb__nav--prev" onClick={() => go(-1)} aria-label={t('biz.photo_prev')}>
              ‹
            </button>
            <button type="button" className="biz-lb__nav biz-lb__nav--next" onClick={() => go(1)} aria-label={t('biz.photo_next')}>
              ›
            </button>
          </>
        )}
      </div>
      {n > 1 && (
        <div className="biz-lb__strip" ref={stripRef}>
          {photos.map((p, i) => (
            <button
              key={i}
              type="button"
              className={i === idx ? 'biz-lb__thumb is-on' : 'biz-lb__thumb'}
              aria-current={i === idx || undefined}
              aria-label={t('biz.photo_open', { n: localiseDigits(lang, i + 1), total: localiseDigits(lang, n) })}
              onClick={() => setIdx(i)}
            >
              <img src={p.thumb} alt="" loading="lazy" decoding="async" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
